import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
  clearPatientPHI,
  createPatientPHI,
  getPatientPHIById,
  updatePatientPHI,
} from '../../apis/patientPHISlice';
import PatientPHIStyle from './PatientPHI.module.scss';

const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PIN_REGEX = /^\d{6}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const accordionSx = {
  mb: 1,
  borderRadius: '8px !important',
  border: '1px solid var(--color-border-soft)',
  boxShadow: 'none',
  '&:before': { display: 'none' },
};

const toDateInput = (value) => (value ? moment(value).format('YYYY-MM-DD') : '');

const emptyForm = () => ({
  fullName: '',
  dateOfBirth: '',
  age: '',
  gender: '',
  mobileNumber: '',
  whatsappNumber: '',
  email: '',
  bloodGroup: '',
  dnaTestReport: '',
  maritalStatus: '',
  occupation: '',
  address: '',
  city: '',
  state: '',
  pinCode: '',
  emergencyContact: {
    contactPersonName: '',
    relationship: '',
    mobileNumber: '',
    alternateContactNumber: '',
  },
  basicHealth: {
    heightCm: '',
    weightKg: '',
    bmi: '',
    bloodPressure: '',
    pulseRate: '',
    temperature: '',
    spo2: '',
    knownAllergies: '',
  },
  currentMedicalConditions: {
    diabetes: false,
    highBloodPressure: false,
    heartDisease: false,
    thyroidDisorder: false,
    asthmaRespiratory: false,
    kidneyDisease: false,
    liverDisease: false,
    cancer: false,
    arthritisJoint: false,
    stroke: false,
    highCholesterol: false,
    otherCondition: '',
  },
  surgeryHistory: {
    hasSurgery: false,
    surgeries: [],
    previousHospitalizations: '',
  },
  currentMedications: {
    takingRegularMedicines: false,
    medications: [],
  },
  vaccinationHistory: {
    covid19: false,
    covid19DoseDetails: '',
    tetanus: false,
    hepatitisB: false,
    influenza: false,
    otherVaccines: '',
  },
  familyMedicalHistory: {
    diabetes: false,
    heartDisease: false,
    cancer: false,
    hypertension: false,
    geneticDisorder: false,
    otherHereditaryIllness: '',
  },
  lifestyleHabits: {
    smoking: false,
    alcoholConsumption: false,
    tobaccoGutka: false,
    exercise: '',
    sleepDurationHours: '',
    stressLevel: '',
    dietType: '',
  },
  preventiveHealthHistory: {
    lastHealthCheckupDate: '',
    lastBloodTestDate: '',
    lastECGDate: '',
    lastXRayScanDate: '',
    lastDentalCheckupDate: '',
    lastEyeCheckupDate: '',
  },
  geneticHealthInfo: {
    geneticTestingDone: false,
    geneticTestingDetails: '',
    familyGeneticDisorders: '',
    organDonor: false,
    bloodTransfusionHistory: false,
  },
  womenHealth: {
    menstrualCycleRegular: false,
    pregnancyHistory: '',
    gynecologicalDisease: '',
    lastPapSmearTest: '',
  },
  doctorNotes: {
    symptomsComplaints: '',
    doctorObservation: '',
    recommendedTests: '',
    adviceTreatmentPlan: '',
    doctorName: '',
  },
  insuranceDetails: {
    insuranceCompany: '',
    policyNumber: '',
    policyHolderName: '',
    coverageAmount: '',
    tpaName: '',
  },
  consent: {
    patientName: '',
    consentGiven: false,
    consentDate: '',
  },
});

function mapRecordToForm(record) {
  const base = emptyForm();
  if (!record) return base;
  return {
    ...base,
    ...record,
    dateOfBirth: toDateInput(record.dateOfBirth),
    age: record.age ?? '',
    gender: record.gender || '',
    bloodGroup: record.bloodGroup || '',
    maritalStatus: record.maritalStatus || '',
    emergencyContact: { ...base.emergencyContact, ...(record.emergencyContact || {}) },
    basicHealth: {
      ...base.basicHealth,
      ...(record.basicHealth || {}),
      heightCm: record.basicHealth?.heightCm ?? '',
      weightKg: record.basicHealth?.weightKg ?? '',
      bmi: record.basicHealth?.bmi ?? '',
    },
    currentMedicalConditions: {
      ...base.currentMedicalConditions,
      ...(record.currentMedicalConditions || {}),
    },
    surgeryHistory: {
      ...base.surgeryHistory,
      ...(record.surgeryHistory || {}),
      surgeries: record.surgeryHistory?.surgeries || [],
    },
    currentMedications: {
      ...base.currentMedications,
      ...(record.currentMedications || {}),
      medications: record.currentMedications?.medications || [],
    },
    vaccinationHistory: { ...base.vaccinationHistory, ...(record.vaccinationHistory || {}) },
    familyMedicalHistory: { ...base.familyMedicalHistory, ...(record.familyMedicalHistory || {}) },
    lifestyleHabits: {
      ...base.lifestyleHabits,
      ...(record.lifestyleHabits || {}),
      sleepDurationHours: record.lifestyleHabits?.sleepDurationHours ?? '',
    },
    preventiveHealthHistory: {
      ...base.preventiveHealthHistory,
      lastHealthCheckupDate: toDateInput(record.preventiveHealthHistory?.lastHealthCheckupDate),
      lastBloodTestDate: toDateInput(record.preventiveHealthHistory?.lastBloodTestDate),
      lastECGDate: toDateInput(record.preventiveHealthHistory?.lastECGDate),
      lastXRayScanDate: toDateInput(record.preventiveHealthHistory?.lastXRayScanDate),
      lastDentalCheckupDate: toDateInput(record.preventiveHealthHistory?.lastDentalCheckupDate),
      lastEyeCheckupDate: toDateInput(record.preventiveHealthHistory?.lastEyeCheckupDate),
    },
    geneticHealthInfo: { ...base.geneticHealthInfo, ...(record.geneticHealthInfo || {}) },
    womenHealth: {
      ...base.womenHealth,
      ...(record.womenHealth || {}),
      lastPapSmearTest: toDateInput(record.womenHealth?.lastPapSmearTest),
    },
    doctorNotes: { ...base.doctorNotes, ...(record.doctorNotes || {}) },
    insuranceDetails: {
      ...base.insuranceDetails,
      ...(record.insuranceDetails || {}),
      coverageAmount: record.insuranceDetails?.coverageAmount ?? '',
    },
    consent: {
      ...base.consent,
      ...(record.consent || {}),
      consentDate: toDateInput(record.consent?.consentDate),
    },
  };
}

function calcBmi(heightCm, weightKg) {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!h || !w) return '';
  return (w / (h / 100) ** 2).toFixed(2);
}

function calcAge(dateOfBirth) {
  if (!dateOfBirth) return '';
  const years = moment().diff(moment(dateOfBirth), 'years');
  return years >= 0 ? years : '';
}

function Section({ title, defaultExpanded = false, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={accordionSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography className={PatientPHIStyle.sectionTitle}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <FormControlLabel
        control={<Checkbox checked={!!checked} onChange={(e) => onChange(e.target.checked)} />}
        label={label}
      />
    </Grid>
  );
}

export default function PatientPHIForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { record, loading } = useSelector((state) => state.patientPHIData);

  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      dispatch(getPatientPHIById(id));
    } else {
      dispatch(clearPatientPHI());
      setForm(emptyForm());
    }
    return () => dispatch(clearPatientPHI());
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && record?._id === id) {
      setForm(mapRecordToForm(record));
    }
  }, [isEdit, record, id]);

  const setValue = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        if (cur[keys[i]] == null) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;

      if (path === 'dateOfBirth') {
        next.age = calcAge(value);
      }
      if (path === 'basicHealth.heightCm' || path === 'basicHealth.weightKg') {
        next.basicHealth.bmi = calcBmi(next.basicHealth.heightCm, next.basicHealth.weightKg);
      }
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.fullName?.trim()) nextErrors.fullName = 'Full name is required';
    if (form.mobileNumber && !MOBILE_REGEX.test(form.mobileNumber.trim())) {
      nextErrors.mobileNumber = 'Enter a valid 10-digit Indian mobile number';
    }
    if (form.whatsappNumber && !MOBILE_REGEX.test(form.whatsappNumber.trim())) {
      nextErrors.whatsappNumber = 'Enter a valid 10-digit Indian mobile number';
    }
    if (form.email && !EMAIL_REGEX.test(form.email.trim())) {
      nextErrors.email = 'Enter a valid email address';
    }
    if (form.pinCode && !PIN_REGEX.test(form.pinCode.trim())) {
      nextErrors.pinCode = 'Enter a valid 6-digit PIN code';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields');
      return;
    }

    const payload = structuredClone(form);
    delete payload._id;
    delete payload.__v;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.isDeleted;
    delete payload.createdBy;
    delete payload.patientId;

    let response;
    if (isEdit) {
      response = await dispatch(updatePatientPHI({ id, data: payload }));
    } else {
      response = await dispatch(createPatientPHI(payload));
    }

    if (response?.payload?.success) {
      toast.success(response.payload.message);
      const savedId = response.payload.data?._id || id;
      navigate(`/patient-phi/${savedId}`);
    } else {
      toast.error(response?.payload?.message || 'Unable to save Patient PHI');
    }
  };

  return (
    <div className={PatientPHIStyle.page}>
      <Box className={PatientPHIStyle.pageHead}>
        <Box>
          <Typography className={PatientPHIStyle.pageTitle}>
            {isEdit ? 'Edit Patient PHI' : 'New Patient PHI'}
          </Typography>
          {isEdit && record?.patientId && (
            <Typography className={PatientPHIStyle.pageSubTitle}>{record.patientId}</Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/patient-phi')}
        >
          Back
        </Button>
      </Box>

      <Card className={PatientPHIStyle.formCard}>
        <form onSubmit={handleSubmit}>
          <Section title="1. Personal Details" defaultExpanded>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Full Name"
                  size="small"
                  fullWidth
                  required
                  value={form.fullName}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  onChange={(e) => setValue('fullName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.dateOfBirth}
                  onChange={(e) => setValue('dateOfBirth', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Age"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 130 }}
                  value={form.age}
                  onChange={(e) => setValue('age', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    value={form.gender}
                    onChange={(e) => setValue('gender', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Mobile Number"
                  size="small"
                  fullWidth
                  value={form.mobileNumber}
                  error={!!errors.mobileNumber}
                  helperText={errors.mobileNumber}
                  onChange={(e) => setValue('mobileNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="WhatsApp Number"
                  size="small"
                  fullWidth
                  value={form.whatsappNumber}
                  error={!!errors.whatsappNumber}
                  helperText={errors.whatsappNumber}
                  onChange={(e) => setValue('whatsappNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Email"
                  size="small"
                  fullWidth
                  value={form.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={(e) => setValue('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Blood Group</InputLabel>
                  <Select
                    label="Blood Group"
                    value={form.bloodGroup}
                    onChange={(e) => setValue('bloodGroup', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {BLOOD_GROUPS.map((bg) => (
                      <MenuItem key={bg} value={bg}>
                        {bg}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="DNA Test Report"
                  size="small"
                  fullWidth
                  value={form.dnaTestReport}
                  onChange={(e) => setValue('dnaTestReport', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Marital Status</InputLabel>
                  <Select
                    label="Marital Status"
                    value={form.maritalStatus}
                    onChange={(e) => setValue('maritalStatus', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Single">Single</MenuItem>
                    <MenuItem value="Married">Married</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Occupation"
                  size="small"
                  fullWidth
                  value={form.occupation}
                  onChange={(e) => setValue('occupation', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Address"
                  size="small"
                  fullWidth
                  value={form.address}
                  onChange={(e) => setValue('address', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="City"
                  size="small"
                  fullWidth
                  value={form.city}
                  onChange={(e) => setValue('city', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="State"
                  size="small"
                  fullWidth
                  value={form.state}
                  onChange={(e) => setValue('state', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="PIN Code"
                  size="small"
                  fullWidth
                  value={form.pinCode}
                  error={!!errors.pinCode}
                  helperText={errors.pinCode}
                  onChange={(e) => setValue('pinCode', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="2. Emergency Contact">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Contact Person Name"
                  size="small"
                  fullWidth
                  value={form.emergencyContact.contactPersonName}
                  onChange={(e) => setValue('emergencyContact.contactPersonName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Relationship"
                  size="small"
                  fullWidth
                  value={form.emergencyContact.relationship}
                  onChange={(e) => setValue('emergencyContact.relationship', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Mobile Number"
                  size="small"
                  fullWidth
                  value={form.emergencyContact.mobileNumber}
                  onChange={(e) => setValue('emergencyContact.mobileNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Alternate Contact Number"
                  size="small"
                  fullWidth
                  value={form.emergencyContact.alternateContactNumber}
                  onChange={(e) => setValue('emergencyContact.alternateContactNumber', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="3. Basic Health Information">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Height (cm)"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.basicHealth.heightCm}
                  onChange={(e) => setValue('basicHealth.heightCm', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Weight (kg)"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.basicHealth.weightKg}
                  onChange={(e) => setValue('basicHealth.weightKg', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="BMI"
                  size="small"
                  fullWidth
                  value={form.basicHealth.bmi}
                  onChange={(e) => setValue('basicHealth.bmi', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Blood Pressure"
                  size="small"
                  fullWidth
                  value={form.basicHealth.bloodPressure}
                  onChange={(e) => setValue('basicHealth.bloodPressure', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Pulse Rate"
                  size="small"
                  fullWidth
                  value={form.basicHealth.pulseRate}
                  onChange={(e) => setValue('basicHealth.pulseRate', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Temperature"
                  size="small"
                  fullWidth
                  value={form.basicHealth.temperature}
                  onChange={(e) => setValue('basicHealth.temperature', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="SpO2"
                  size="small"
                  fullWidth
                  value={form.basicHealth.spo2}
                  onChange={(e) => setValue('basicHealth.spo2', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <TextField
                  label="Known Allergies"
                  size="small"
                  fullWidth
                  value={form.basicHealth.knownAllergies}
                  onChange={(e) => setValue('basicHealth.knownAllergies', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="4. Current Medical Conditions">
            <Grid container spacing={1}>
              <Check
                label="Diabetes"
                checked={form.currentMedicalConditions.diabetes}
                onChange={(v) => setValue('currentMedicalConditions.diabetes', v)}
              />
              <Check
                label="High Blood Pressure"
                checked={form.currentMedicalConditions.highBloodPressure}
                onChange={(v) => setValue('currentMedicalConditions.highBloodPressure', v)}
              />
              <Check
                label="Heart Disease"
                checked={form.currentMedicalConditions.heartDisease}
                onChange={(v) => setValue('currentMedicalConditions.heartDisease', v)}
              />
              <Check
                label="Thyroid Disorder"
                checked={form.currentMedicalConditions.thyroidDisorder}
                onChange={(v) => setValue('currentMedicalConditions.thyroidDisorder', v)}
              />
              <Check
                label="Asthma / Respiratory"
                checked={form.currentMedicalConditions.asthmaRespiratory}
                onChange={(v) => setValue('currentMedicalConditions.asthmaRespiratory', v)}
              />
              <Check
                label="Kidney Disease"
                checked={form.currentMedicalConditions.kidneyDisease}
                onChange={(v) => setValue('currentMedicalConditions.kidneyDisease', v)}
              />
              <Check
                label="Liver Disease"
                checked={form.currentMedicalConditions.liverDisease}
                onChange={(v) => setValue('currentMedicalConditions.liverDisease', v)}
              />
              <Check
                label="Cancer"
                checked={form.currentMedicalConditions.cancer}
                onChange={(v) => setValue('currentMedicalConditions.cancer', v)}
              />
              <Check
                label="Arthritis / Joint"
                checked={form.currentMedicalConditions.arthritisJoint}
                onChange={(v) => setValue('currentMedicalConditions.arthritisJoint', v)}
              />
              <Check
                label="Stroke"
                checked={form.currentMedicalConditions.stroke}
                onChange={(v) => setValue('currentMedicalConditions.stroke', v)}
              />
              <Check
                label="High Cholesterol"
                checked={form.currentMedicalConditions.highCholesterol}
                onChange={(v) => setValue('currentMedicalConditions.highCholesterol', v)}
              />
              <Grid item xs={12}>
                <TextField
                  label="Other Condition"
                  size="small"
                  fullWidth
                  value={form.currentMedicalConditions.otherCondition}
                  onChange={(e) => setValue('currentMedicalConditions.otherCondition', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="5. Surgery & Hospitalization History">
            <Grid container spacing={2}>
              <Check
                label="Has Surgery"
                checked={form.surgeryHistory.hasSurgery}
                onChange={(v) => {
                  setValue('surgeryHistory.hasSurgery', v);
                  if (v && form.surgeryHistory.surgeries.length === 0) {
                    setForm((prev) => ({
                      ...prev,
                      surgeryHistory: {
                        ...prev.surgeryHistory,
                        hasSurgery: true,
                        surgeries: [{ surgeryName: '', year: '', hospital: '' }],
                      },
                    }));
                  }
                }}
              />
              <Grid item xs={12}>
                <TextField
                  label="Previous Hospitalizations"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.surgeryHistory.previousHospitalizations}
                  onChange={(e) => setValue('surgeryHistory.previousHospitalizations', e.target.value)}
                />
              </Grid>
            </Grid>
            {form.surgeryHistory.hasSurgery && (
              <Box className={PatientPHIStyle.rowList} sx={{ mt: 1.5 }}>
                {form.surgeryHistory.surgeries.map((item, idx) => (
                  <div key={idx} className={PatientPHIStyle.rowCard}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Surgery Name"
                          size="small"
                          fullWidth
                          value={item.surgeryName}
                          onChange={(e) => setValue(`surgeryHistory.surgeries.${idx}.surgeryName`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Year"
                          size="small"
                          fullWidth
                          value={item.year}
                          onChange={(e) => setValue(`surgeryHistory.surgeries.${idx}.year`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Hospital"
                          size="small"
                          fullWidth
                          value={item.hospital}
                          onChange={(e) => setValue(`surgeryHistory.surgeries.${idx}.hospital`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              surgeryHistory: {
                                ...prev.surgeryHistory,
                                surgeries: prev.surgeryHistory.surgeries.filter((_, i) => i !== idx),
                              },
                            }))
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      surgeryHistory: {
                        ...prev.surgeryHistory,
                        surgeries: [...prev.surgeryHistory.surgeries, { surgeryName: '', year: '', hospital: '' }],
                      },
                    }))
                  }
                >
                  Add Surgery
                </Button>
              </Box>
            )}
          </Section>

          <Section title="6. Current Medications">
            <Grid container spacing={2}>
              <Check
                label="Taking Regular Medicines"
                checked={form.currentMedications.takingRegularMedicines}
                onChange={(v) => {
                  if (v && form.currentMedications.medications.length === 0) {
                    setForm((prev) => ({
                      ...prev,
                      currentMedications: {
                        ...prev.currentMedications,
                        takingRegularMedicines: true,
                        medications: [{ medicineName: '', dosage: '', reason: '' }],
                      },
                    }));
                  } else {
                    setValue('currentMedications.takingRegularMedicines', v);
                  }
                }}
              />
            </Grid>
            {form.currentMedications.takingRegularMedicines && (
              <Box className={PatientPHIStyle.rowList} sx={{ mt: 1.5 }}>
                {form.currentMedications.medications.map((item, idx) => (
                  <div key={idx} className={PatientPHIStyle.rowCard}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Medicine Name"
                          size="small"
                          fullWidth
                          value={item.medicineName}
                          onChange={(e) =>
                            setValue(`currentMedications.medications.${idx}.medicineName`, e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Dosage"
                          size="small"
                          fullWidth
                          value={item.dosage}
                          onChange={(e) => setValue(`currentMedications.medications.${idx}.dosage`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Reason"
                          size="small"
                          fullWidth
                          value={item.reason}
                          onChange={(e) => setValue(`currentMedications.medications.${idx}.reason`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton
                          color="error"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              currentMedications: {
                                ...prev.currentMedications,
                                medications: prev.currentMedications.medications.filter((_, i) => i !== idx),
                              },
                            }))
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      currentMedications: {
                        ...prev.currentMedications,
                        medications: [
                          ...prev.currentMedications.medications,
                          { medicineName: '', dosage: '', reason: '' },
                        ],
                      },
                    }))
                  }
                >
                  Add Medicine
                </Button>
              </Box>
            )}
          </Section>

          <Section title="7. Vaccination History">
            <Grid container spacing={1}>
              <Check
                label="COVID-19"
                checked={form.vaccinationHistory.covid19}
                onChange={(v) => setValue('vaccinationHistory.covid19', v)}
              />
              <Grid item xs={12} sm={8}>
                <TextField
                  label="COVID-19 Dose Details"
                  size="small"
                  fullWidth
                  value={form.vaccinationHistory.covid19DoseDetails}
                  onChange={(e) => setValue('vaccinationHistory.covid19DoseDetails', e.target.value)}
                />
              </Grid>
              <Check
                label="Tetanus"
                checked={form.vaccinationHistory.tetanus}
                onChange={(v) => setValue('vaccinationHistory.tetanus', v)}
              />
              <Check
                label="Hepatitis B"
                checked={form.vaccinationHistory.hepatitisB}
                onChange={(v) => setValue('vaccinationHistory.hepatitisB', v)}
              />
              <Check
                label="Influenza"
                checked={form.vaccinationHistory.influenza}
                onChange={(v) => setValue('vaccinationHistory.influenza', v)}
              />
              <Grid item xs={12}>
                <TextField
                  label="Other Vaccines"
                  size="small"
                  fullWidth
                  value={form.vaccinationHistory.otherVaccines}
                  onChange={(e) => setValue('vaccinationHistory.otherVaccines', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="8. Family Medical History">
            <Grid container spacing={1}>
              <Check
                label="Diabetes"
                checked={form.familyMedicalHistory.diabetes}
                onChange={(v) => setValue('familyMedicalHistory.diabetes', v)}
              />
              <Check
                label="Heart Disease"
                checked={form.familyMedicalHistory.heartDisease}
                onChange={(v) => setValue('familyMedicalHistory.heartDisease', v)}
              />
              <Check
                label="Cancer"
                checked={form.familyMedicalHistory.cancer}
                onChange={(v) => setValue('familyMedicalHistory.cancer', v)}
              />
              <Check
                label="Hypertension"
                checked={form.familyMedicalHistory.hypertension}
                onChange={(v) => setValue('familyMedicalHistory.hypertension', v)}
              />
              <Check
                label="Genetic Disorder"
                checked={form.familyMedicalHistory.geneticDisorder}
                onChange={(v) => setValue('familyMedicalHistory.geneticDisorder', v)}
              />
              <Grid item xs={12}>
                <TextField
                  label="Other Hereditary Illness"
                  size="small"
                  fullWidth
                  value={form.familyMedicalHistory.otherHereditaryIllness}
                  onChange={(e) => setValue('familyMedicalHistory.otherHereditaryIllness', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="9. Lifestyle Habits">
            <Grid container spacing={2}>
              <Check label="Smoking" checked={form.lifestyleHabits.smoking} onChange={(v) => setValue('lifestyleHabits.smoking', v)} />
              <Check
                label="Alcohol Consumption"
                checked={form.lifestyleHabits.alcoholConsumption}
                onChange={(v) => setValue('lifestyleHabits.alcoholConsumption', v)}
              />
              <Check
                label="Tobacco / Gutka"
                checked={form.lifestyleHabits.tobaccoGutka}
                onChange={(v) => setValue('lifestyleHabits.tobaccoGutka', v)}
              />
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Exercise</InputLabel>
                  <Select
                    label="Exercise"
                    value={form.lifestyleHabits.exercise}
                    onChange={(e) => setValue('lifestyleHabits.exercise', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Regular">Regular</MenuItem>
                    <MenuItem value="Occasional">Occasional</MenuItem>
                    <MenuItem value="None">None</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Sleep Duration (hours)"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.lifestyleHabits.sleepDurationHours}
                  onChange={(e) => setValue('lifestyleHabits.sleepDurationHours', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Stress Level</InputLabel>
                  <Select
                    label="Stress Level"
                    value={form.lifestyleHabits.stressLevel}
                    onChange={(e) => setValue('lifestyleHabits.stressLevel', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Moderate">Moderate</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Diet Type</InputLabel>
                  <Select
                    label="Diet Type"
                    value={form.lifestyleHabits.dietType}
                    onChange={(e) => setValue('lifestyleHabits.dietType', e.target.value)}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Vegetarian">Vegetarian</MenuItem>
                    <MenuItem value="Non-Vegetarian">Non-Vegetarian</MenuItem>
                    <MenuItem value="Vegan">Vegan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Section>

          <Section title="10. Preventive Health History">
            <Grid container spacing={2}>
              {[
                ['lastHealthCheckupDate', 'Last Health Checkup'],
                ['lastBloodTestDate', 'Last Blood Test'],
                ['lastECGDate', 'Last ECG'],
                ['lastXRayScanDate', 'Last X-Ray / Scan'],
                ['lastDentalCheckupDate', 'Last Dental Checkup'],
                ['lastEyeCheckupDate', 'Last Eye Checkup'],
              ].map(([key, label]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <TextField
                    label={label}
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.preventiveHealthHistory[key]}
                    onChange={(e) => setValue(`preventiveHealthHistory.${key}`, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Section>

          <Section title="11. Genetic / Advanced Health Information">
            <Grid container spacing={2}>
              <Check
                label="Genetic Testing Done"
                checked={form.geneticHealthInfo.geneticTestingDone}
                onChange={(v) => setValue('geneticHealthInfo.geneticTestingDone', v)}
              />
              <Check
                label="Organ Donor"
                checked={form.geneticHealthInfo.organDonor}
                onChange={(v) => setValue('geneticHealthInfo.organDonor', v)}
              />
              <Check
                label="Blood Transfusion History"
                checked={form.geneticHealthInfo.bloodTransfusionHistory}
                onChange={(v) => setValue('geneticHealthInfo.bloodTransfusionHistory', v)}
              />
              <Grid item xs={12} md={6}>
                <TextField
                  label="Genetic Testing Details"
                  size="small"
                  fullWidth
                  value={form.geneticHealthInfo.geneticTestingDetails}
                  onChange={(e) => setValue('geneticHealthInfo.geneticTestingDetails', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Family Genetic Disorders"
                  size="small"
                  fullWidth
                  value={form.geneticHealthInfo.familyGeneticDisorders}
                  onChange={(e) => setValue('geneticHealthInfo.familyGeneticDisorders', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          {form.gender === 'Female' && (
            <Section title="12. Women's Health" defaultExpanded>
              <Grid container spacing={2}>
                <Check
                  label="Menstrual Cycle Regular"
                  checked={form.womenHealth.menstrualCycleRegular}
                  onChange={(v) => setValue('womenHealth.menstrualCycleRegular', v)}
                />
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pregnancy History"
                    size="small"
                    fullWidth
                    value={form.womenHealth.pregnancyHistory}
                    onChange={(e) => setValue('womenHealth.pregnancyHistory', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Gynecological Disease"
                    size="small"
                    fullWidth
                    value={form.womenHealth.gynecologicalDisease}
                    onChange={(e) => setValue('womenHealth.gynecologicalDisease', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Last Pap Smear Test"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.womenHealth.lastPapSmearTest}
                    onChange={(e) => setValue('womenHealth.lastPapSmearTest', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Section>
          )}

          <Section title="13. Doctor / Health Advisor Notes">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Symptoms / Complaints"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.doctorNotes.symptomsComplaints}
                  onChange={(e) => setValue('doctorNotes.symptomsComplaints', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Doctor Observation"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.doctorNotes.doctorObservation}
                  onChange={(e) => setValue('doctorNotes.doctorObservation', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Recommended Tests"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.doctorNotes.recommendedTests}
                  onChange={(e) => setValue('doctorNotes.recommendedTests', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Advice / Treatment Plan"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.doctorNotes.adviceTreatmentPlan}
                  onChange={(e) => setValue('doctorNotes.adviceTreatmentPlan', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Doctor Name"
                  size="small"
                  fullWidth
                  value={form.doctorNotes.doctorName}
                  onChange={(e) => setValue('doctorNotes.doctorName', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="14. Insurance / Mediclaim Details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Insurance Company"
                  size="small"
                  fullWidth
                  value={form.insuranceDetails.insuranceCompany}
                  onChange={(e) => setValue('insuranceDetails.insuranceCompany', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Policy Number"
                  size="small"
                  fullWidth
                  value={form.insuranceDetails.policyNumber}
                  onChange={(e) => setValue('insuranceDetails.policyNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Policy Holder Name"
                  size="small"
                  fullWidth
                  value={form.insuranceDetails.policyHolderName}
                  onChange={(e) => setValue('insuranceDetails.policyHolderName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Coverage Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={form.insuranceDetails.coverageAmount}
                  onChange={(e) => setValue('insuranceDetails.coverageAmount', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="TPA Name"
                  size="small"
                  fullWidth
                  value={form.insuranceDetails.tpaName}
                  onChange={(e) => setValue('insuranceDetails.tpaName', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="15. Consent & Declaration">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Patient Name"
                  size="small"
                  fullWidth
                  value={form.consent.patientName}
                  onChange={(e) => setValue('consent.patientName', e.target.value)}
                />
              </Grid>
              <Check
                label="Consent Given"
                checked={form.consent.consentGiven}
                onChange={(v) => setValue('consent.consentGiven', v)}
              />
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Consent Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.consent.consentDate}
                  onChange={(e) => setValue('consent.consentDate', e.target.value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/patient-phi')}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              className={PatientPHIStyle.addBtn}
            >
              {isEdit ? 'Update' : 'Save'}
            </LoadingButton>
          </Box>
        </form>
      </Card>
    </div>
  );
}
